import { PaginationProps, TableProps } from 'antd';
import { getApi } from 'apis';
import TableActionHeader from 'components/Global/TableActionHeader';
import { ChangeEvent, useCallback, useEffect, useState } from 'react';
import { toastText } from 'utils/utils';
import { useDebounce } from 'utils/hooks';
import CustomTable from './CustomTable';

const StaffTable = () => {
	const [search, setSearch] = useState('');
	const [page, setPage] = useState(1);
	const [limit, setLimit] = useState(
		Number(localStorage.getItem('staffTableSize')) || 10
	);
	const [sort, setSort] = useState('');
	const [type, setType] = useState('asc');
	const [staffSegment, setStaffSegment] = useState<'active' | 'inactive'>('active');
	const [totalStaff, setTotalStaff] = useState(0);
	const [staffData, setStaffData] = useState([]);
	const [isLoading, setIsLoading] = useState(false);
	const [isSearchCancel, setIsSearchCancel] = useState<boolean>(false);

	const debouncedSearch = useDebounce(search, 500);

	const performSearchHandler = (event: ChangeEvent<HTMLInputElement>) => {
		const { value } = event.target;
		setSearch(value);
		setPage(1);
		if (value.length > 0) {
			setIsSearchCancel(true);
		} else {
			setIsSearchCancel(false);
		}
	};

	const fetchStaff = useCallback(async () => {
		setIsLoading(true);
		const query = {
			page,
			limit,
			search: debouncedSearch,
			sort,
			type,
			staffActive: staffSegment === 'active' ? 'true' : 'false',
		};
		try {
			const res = await getApi('/user/staff', query);
			setStaffData(res?.data?.data);
			setTotalStaff(res?.data?.total);
		} catch (error: any) {
			toastText(
				error?.response?.data?.error?.errorDescription ??
					error?.response?.data?.message ??
					'Something went wrong fetching staff',
				'error'
			);
		} finally {
			setIsLoading(false);
		}
	}, [page, limit, debouncedSearch, type, sort, staffSegment]);

	const handleTableChange: TableProps<any>['onChange'] = (
		_pagination,
		_filters,
		sorter
	) => {
		const sortOrder = Array.isArray(sorter)
			? sorter[0]?.order
			: sorter.order === 'ascend'
				? 'asc'
				: 'desc';
		setType(sortOrder as string);

		const sortField = Array.isArray(sorter)
			? sorter[0]?.field
			: sorter.field === 'name'
				? 'firstName'
				: sorter.field;
		setSort(sortField as string);
	};

	const onShowSizeChange: PaginationProps['onShowSizeChange'] = (
		current,
		pageSize
	) => {
		setPage(current);
		setLimit(pageSize);
		localStorage.setItem('staffTableSize', String(pageSize));
	};

	const handleStaffSegmentChange = (segment: 'active' | 'inactive') => {
		setStaffSegment(segment);
		setPage(1);
	};

	useEffect(() => {
		fetchStaff();
	}, [limit, page, debouncedSearch, sort, type, staffSegment, fetchStaff]);

	return (
		<>
			<TableActionHeader title={'Staff'} />
			<CustomTable
				dataSource={staffData}
				handleTableChange={handleTableChange}
				onShowSizeChange={onShowSizeChange}
				isLoading={isLoading}
				search={search}
				totalRecords={totalStaff}
				performSearchHandler={performSearchHandler}
				staffSegment={staffSegment}
				onStaffSegmentChange={handleStaffSegmentChange}
				onStatusChange={fetchStaff}
				currentPage={page}
				pageSize={limit}
				isSearchCancel={isSearchCancel}
				setIsSearchCancel={setIsSearchCancel}
				setSearch={setSearch}
			/>
		</>
	);
};

export default StaffTable;
